import React, { useState } from "react";
import { Button, Form, Table, Card, Spinner, Alert } from "react-bootstrap";
import axios from "axios";

const SYSTEM_FIELDS = [
  { key: "companyName", label: "Company Name *", required: true },
  { key: "contactPerson", label: "Contact Person *", required: true },
  { key: "mobileNo", label: "Mobile No. *", required: true },
  { key: "email", label: "Email *", required: true },
  { key: "stallSize", label: "Stall Size *", required: true },
  { key: "stallNo", label: "Stall No.", required: false }
];

const ImportStep1 = ({
  uploadedFile,
  setUploadedFile,
  parsedHeaders,
  setParsedHeaders,
  columnMapping,
  setColumnMapping,
  setValidationResult,
  onNext,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/exhibitors/import/template`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "exhibitors_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      setError("Failed to download template.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Reset previous states when a new file is selected
    setValidationResult(null);
    setParsedHeaders([]);
    setColumnMapping({});
    
    setUploadedFile(file);
    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/exhibitors/import/parse-headers`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (res.data.status) {
        setParsedHeaders(res.data.headers);
        autoMapHeaders(res.data.headers);
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error parsing file headers.");
    } finally {
      setLoading(false);
    }
  };

  const autoMapHeaders = (headers) => {
    const newMapping = {};
    SYSTEM_FIELDS.forEach((sysField) => {
      const lowerSys = sysField.label.toLowerCase().replace(/[^a-z]/g, "");
      const match = headers.find((h) => {
        if (!h) return false;
        const lowerH = String(h).toLowerCase().replace(/[^a-z]/g, "");
        return lowerH.includes(lowerSys) || lowerSys.includes(lowerH);
      });
      if (match) {
        newMapping[sysField.key] = match;
      } else {
        newMapping[sysField.key] = "";
      }
    });
    setColumnMapping(newMapping);
  };

  const handleMappingChange = (sysKey, headerValue) => {
    setColumnMapping((prev) => ({ ...prev, [sysKey]: headerValue }));
  };

  const isNextDisabled = () => {
    return SYSTEM_FIELDS.filter((f) => f.required).some(
      (f) => !columnMapping[f.key]
    );
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h5 className="mb-0">Step 1: Upload & Map Columns</h5>
          <Button
            variant="outline-primary"
            onClick={handleDownloadTemplate}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "Download Template"}
          </Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Upload Data File (.xlsx, .csv)</Form.Label>
          <Form.Control
            type="file"
            accept=".xlsx, .xls, .csv"
            onChange={handleFileUpload}
          />
        </Form.Group>

        {loading && <Spinner animation="border" variant="primary" />}

        {parsedHeaders.length > 0 && !loading && (
          <>
            <h6 className="fw-bold mb-3">Map Columns</h6>
            <Table bordered hover responsive>
              <thead className="bg-light">
                <tr>
                  <th>System Field</th>
                  <th>File Column Header</th>
                </tr>
              </thead>
              <tbody>
                {SYSTEM_FIELDS.map((field) => (
                  <tr key={field.key}>
                    <td className={field.required ? "fw-bold text-danger" : ""}>
                      {field.label}
                    </td>
                    <td>
                      <Form.Select
                        value={columnMapping[field.key] || ""}
                        onChange={(e) =>
                          handleMappingChange(field.key, e.target.value)
                        }
                      >
                        <option value="">— Skip / Unmapped —</option>
                        {parsedHeaders.map((header, idx) => (
                          <option key={idx} value={header}>
                            {header}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="primary" onClick={onNext} disabled={isNextDisabled()}>
                Next {"→"}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImportStep1;
